import React, { Dispatch, ReactElement, SetStateAction } from 'react';
import {
  ActivityIndicator, Modal, Pressable, StyleSheet, Text, View,
} from 'react-native';
import Colors from '../constants/Colors';
import AlertIcon from './AlertIcon';
import ResolvedIcon from './ResolvedIcon';
import AlertIconMobile from './16pxIcons/AlertIconMobile';
import ResolvedIconMobile from './16pxIcons/ResolvedIconMobile';
import Style from '../constants/Style';
import isMobile from './IsMobile';
import { logEvent } from './LogEvent';
import Button from './Button';
import PressableStyled from './PressableStyled';
import CloseModalIcon from './CloseModalIcon';

interface AlertModalProps {
  modalVisible: boolean,
  setModalVisible: Dispatch<SetStateAction<boolean>>,
  title: string,
  heading?: string,
  message?: string | ReactElement,
  error?: boolean,
  action?: () => void,
  actionTitle?: string,
  cancelTitle?: string,
  closeTitle?: string,
  onModalHide?: () => void,
  loading?: boolean,
  isMessage?: boolean,
  cancelAction?: () => void,
  mobileFullScreen?: boolean,
  autoClose?: boolean,
}

export default function AlertModal({
  modalVisible, setModalVisible, title, message, error = true, action, actionTitle, closeTitle,
  onModalHide, loading = false, isMessage = false, cancelTitle, heading,
  cancelAction, mobileFullScreen, autoClose = false,
}: AlertModalProps) {
  React.useEffect(() => {
    if (modalVisible && autoClose) {
      const timer = setTimeout(() => {
        setModalVisible(false);
      }, 1100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [modalVisible, autoClose]);

  const renderClose = () => {
    const showActionButton: boolean = !!action && !!actionTitle;

    return <View style={styles.closeButtonContainer}>
      <Button
        affirmative={!(action && actionTitle)}
        style={{
          ...styles.mainButton,
          ...((showActionButton && isMobile) ? { flex: 1 } : {}),
        }}
        onPress={() => {
          logEvent('AlertModal', 'close_alert_modal');
          if (cancelAction) cancelAction(); else if (!actionTitle && action) action();
          setModalVisible(false);
        }}
        title={showActionButton ? cancelTitle ?? 'Cancel' : closeTitle ?? 'Close'}
        testID='close-pop-up-modal-btn'
      />
      {showActionButton && <Button
        affirmative
        onPress={() => {
          logEvent('AlertModal', 'alert_modal_action');
          setModalVisible(false);
          action();
        }}
        title={actionTitle}
        testID='alert-modal-confirm-button'
      />}
    </View>;
  };

  if (mobileFullScreen) {
    return loading ? <ActivityIndicator
      size='large'
      animating={loading}
      style={Style.activityIndicator}
    /> : <Modal
      {...{ onModalHide }}
      style={[StyleSheet.absoluteFillObject, {
        backgroundColor: '#fff',
        justifyContent: 'center',
        marginTop: 60,
      }]}
      presentationStyle='formSheet'
      visible={modalVisible}
      onRequestClose={() => setModalVisible(false)}
    >
      <View
        style={[
          styles.containerMobileFullScreen,
          error && styles.containerError, isMessage && styles.containerMessage,
        ]}
        testID='pop-up-modal'
      >
        <Text style={{
          fontFamily: 'Poppins_600SemiBold',
          fontSize: 14,
          lineHeight: 27,
          color: Colors.light.hereworksBlack600,
          marginBottom: 40,
          marginTop: 20,
        }}>
          {heading ?? title}</Text>
        <View style={[
          styles.iconContainer, error && styles.iconContainerError,
          isMessage && styles.isMessageContainerError,
        ]}>
          {error && <AlertIconMobile />}
          {!error && <ResolvedIconMobile />}
        </View>
        <Text style={[
          Style.title, styles.title, error && styles.titleError, isMessage && styles.messageTitle,
        ]}>
          {title}</Text>
        {message && <Text style={styles.description}>{message}</Text>}
        {renderClose()}
      </View>
      <PressableStyled
        onPress={() => {
          logEvent('AlertModal', 'close_alert_modal');
          if (cancelAction) cancelAction();
          setModalVisible(false);
        }}
        style={{
          position: 'absolute',
          top: 20,
          right: 20,
        }}
      >
        <CloseModalIcon />
      </PressableStyled>
    </Modal>;
  }

  return <Modal
    transparent
    visible={modalVisible}
    animationType='fade'
    {...{ onModalHide }}
    style={{ alignItems: 'center' }}
    onRequestClose={() => setModalVisible(false)}
  >
    {loading ? <ActivityIndicator
      size='large'
      animating={loading}
      style={Style.activityIndicator}
    /> : <>
      <Pressable
        style={{ flex: 1, justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.2)' }}
        onPress={() => {
          logEvent('AlertModal', 'close_alert_modal');
          setModalVisible(false);
        }}>
        <View
          style={[
            styles.container, error && styles.containerError, isMessage && styles.containerMessage,
          ]}
          testID='pop-up-modal'
        >
          <View style={[
            styles.iconContainer, error && styles.iconContainerError,
            isMessage && styles.isMessageContainerError,
          ]}>
            {error && (isMobile ? <AlertIconMobile /> : <AlertIcon />)}
            {!error && (isMobile ? <ResolvedIconMobile /> : <ResolvedIcon />)}
          </View>
          <Text style={[
            Style.title, styles.title, error && styles.titleError, isMessage && styles.messageTitle,
          ]}>
            {title}</Text>
          {message && <Text style={styles.description}>{message}</Text>}
          {!autoClose && renderClose()}
        </View>
      </Pressable>
    </>}
  </Modal>;
}

const styles = StyleSheet.create({
  container: {
    alignSelf: 'center',
    borderRadius: 10,
    backgroundColor: '#fff',
    padding: isMobile ? 16 : 40,
    margin: 10,
    borderWidth: 1,
    borderColor: Colors.light.hereworksGreen400,
    width: isMobile ? 265 : 590,
    maxWidth: '95%',
    alignItems: 'center',
    shadowRadius: 20,
    shadowOffset: { width: 0, height: 8 },
    elevation: 8,
    shadowOpacity: 0.05,
    shadowColor: '#000',
  },
  containerMobileFullScreen: {
    flex: 1,
    alignItems: 'center',
    paddingHorizontal: 40,
  },
  containerMessage: {
    borderColor: Colors.light.hereworksBlue500,
  },
  containerError: {
    borderColor: Colors.light.hereworksRed500,
  },
  iconContainer: {
    width: isMobile ? 40 : 64,
    height: isMobile ? 40 : 64,
    borderRadius: 32,
    backgroundColor: Colors.light.hereworksGreen400,
    justifyContent: 'center',
    alignItems: 'center',
  },
  iconContainerError: {
    backgroundColor: Colors.light.hereworksRed500,
  },
  isMessageContainerError: {
    backgroundColor: Colors.light.hereworksBlue500,
    borderColor: Colors.light.hereworksBlue500,
  },
  title: {
    color: Colors.light.hereworksGreen400,
    marginTop: isMobile ? 24 : 32,
  },
  titleError: {
    color: Colors.light.hereworksRed500,
  },
  messageTitle: {
    color: Colors.light.hereworksBlue500,
  },
  description: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 14,
    lineHeight: 22,
    color: Colors.light.hereworksBlack400,
    marginTop: 12,
    textAlign: 'center',
    width: '100%',
  },
  closeButtonContainer: {
    marginTop: isMobile ? 16 : 40,
    flexDirection: 'row',
  },
  mainButton: {
    marginStart: 0,
    flex: undefined,
  },
});
